import type { HsClippingPlane } from '../../../engine-new/objects/clipping/clipping-plane.class';
import { HsShaderDataType } from '../enums/shader-data-type.enum';
import { HS_NORMAL_TRANSFORM_SHADER_CODE } from '../functions/normal-transform-shader-code.constant';
import { HsShaderObjectType } from '../enums/shader-object-type.enum';
import { hsAddVertexUniform } from '../functions/add-vertex-uniform.function';
import { HsShaderClippingFunctions } from '../functions/clipping-shader-functions.function';
import { hsGetMatrixUniform } from '../functions/get-matrix-uniform.function';
import { HsShaderLightFunctionsCode } from '../functions/light-shader-functions-code.constant';
import { HsShaderLightFunctions } from '../functions/shader-light-functions.function';
import { HsDefaultShader, type HsMeshBatchMode } from './default-shader.class';
import type { HsLightStorage } from '../../../engine-new/storages/light-storage.class';
import type { HsClippingStorage } from '../../../engine-new/storages/clipping-storage.class';
import {
  HS_MESH_BATCH_OBJECTS_PER_ROW,
  HS_MESH_BATCH_PIXELS_PER_OBJECT,
} from '../../constants/mesh-batch-layout.constant';

function floatToCode(value: number): string {
  return value % 1 === 0 ? value.toFixed(1) : value.toString();
}

/**
 * Handles s shader data.
 */
export class HsShaderData {
  private readonly vertexFields: string[] = [];
  private readonly fragmentFields: string[] = [];

  private readonly vertexFunctions: string[] = [];
  private readonly fragmentFunctions: string[] = [];

  private readonly vertexMain: string[] = [];
  private readonly fragmentMain: string[] = [];

  private readonly shader: HsDefaultShader;

  constructor(shaderType: HsShaderObjectType) {
    this.shader = new HsDefaultShader(shaderType);
  }

  public addColor(): HsShaderData {
    this.vertexFields.push(hsAddVertexUniform('color', HsShaderDataType.vec3, this.shader.shaderType));
    this.vertexFields.push('out vec3 v_color;');
    this.vertexMain.push(hsGetMatrixUniform('color', HsShaderDataType.vec3, this.shader.shaderType));
    this.vertexMain.push('v_color = color;');
    this.fragmentFields.push('in vec3 v_color;');
    this.fragmentMain.push('color.rgb = v_color;');
    return this;
  }

  public addDepth(): HsShaderData {
    this.vertexFields.push('out float v_fragDepth;');
    this.vertexMain.push('v_fragDepth = 1.0 + gl_Position.w;');
    this.fragmentFields.push('uniform float u_logDepthBufFC;');
    this.fragmentFields.push('in float v_fragDepth;');
    this.fragmentMain.push('gl_FragDepth = log2(v_fragDepth) * u_logDepthBufFC * 0.5;');
    return this;
  }

  /**
   * Log depth for point sprites, written through `gl_Position` instead of `gl_FragDepth`. A point has one
   * depth, so the vertex value equals what `addDepth` would write per fragment, and leaving `gl_FragDepth`
   * alone keeps early depth testing: overlapped point fragments are rejected before they are shaded.
   */
  public addPointDepth(): HsShaderData {
    this.vertexFields.push('uniform float u_logDepthBufFC;');
    this.vertexMain.push(
      'gl_Position.z = (log2(max(1.0 + gl_Position.w, 1e-6)) * u_logDepthBufFC - 1.0) * gl_Position.w;'
    );
    return this;
  }

  public addNormals(): HsShaderData {
    this.vertexFunctions.push(HS_NORMAL_TRANSFORM_SHADER_CODE);
    this.vertexFields.push('in vec3 a_normals;');
    this.vertexFields.push('out vec3 v_normal;');
    this.vertexMain.push('v_normal = transformSurfaceNormal(mat3(relativeMatrix), a_normals);');
    this.fragmentFields.push('in vec3 v_normal;');
    this.fragmentMain.push('vec3 normal = normalize(v_normal);');
    return this;
  }

  public addTransparency(alpha: number): HsShaderData {
    this.fragmentMain.push(`color = vec4(color.rgb, ${floatToCode(alpha)});`);
    this.shader.hasTransparency = true;
    return this;
  }

  public addLumaTransparency(): HsShaderData {
    this.fragmentMain.push('float alpha = length(pow(clamp(color.rgb, 0.0, 1.0), vec3(2.0)));');
    this.fragmentMain.push('color = vec4(color.rgb, alpha);');
    this.shader.hasTransparency = true;
    return this;
  }

  public addDiffuseLighting(): HsShaderData {
    this.vertexFunctions.push(HsShaderLightFunctionsCode.DIFFUSE_LIGHT_SHADER_FUNCTIONS_CODE);
    this.vertexFields.push(HsShaderLightFunctions.makeUniformArrays(this.shader.lightStorage.lights));
    // Point and spot lights take the negated world position, the space light positions are given in.
    this.vertexFunctions.push(`vec3 createDiffuseLights(vec3 normal, vec3 position) {
      vec3 lightColor = vec3(1.0, 1.0, 1.0);
      vec3 diffuse = vec3(0.0);
      ${HsShaderLightFunctions.makeLight(this.shader.lightStorage.lights, false)}
      return diffuse + ${HsShaderLightFunctions.makeAmbientLight(this.shader.lightStorage.lights)};
    }`);
    this.vertexMain.push('v_color *= createDiffuseLights(v_normal, v_position.xyz);');
    return this;
  }

  public addClipping(): HsShaderData {
    const clippingPlanes: HsClippingPlane[] = this.shader.clippingStorage.clippingPlanes;
    this.fragmentFields.push(HsShaderClippingFunctions.makeUniformArrays(clippingPlanes));
    this.fragmentMain.push(HsShaderClippingFunctions.makeClippingCode(clippingPlanes));
    return this;
  }

  public addRayIntersection(): HsShaderData {
    this.vertexFields.push('out vec4 v_point;');
    this.vertexMain.push('v_point = relativeMatrix * vec4(a_positions, 1.0);');
    this.fragmentFields.push('in vec4 v_point;');
    this.fragmentMain.push('vec4 intersectionPoint = v_point / v_point.w;');
    this.fragmentMain.push('vec3 intersectionDirection = normalize(intersectionPoint.xyz);');
    return this;
  }

  /**
   * Adds specular lighting.
   * @param smoothness smoothness.
   * @returns Result value.
   */
  public addSpecularLighting(smoothness: number = 4, intensity: number = 1): HsShaderData {
    smoothness = Math.pow(4, smoothness - 1);
    const smoothnessString: string = smoothness % 1 === 0 ? smoothness.toFixed(1) : smoothness.toString();
    const intensityString: string = floatToCode(intensity);
    this.fragmentFields.push(`float smoothness = ${smoothnessString};`);
    this.fragmentFunctions.push(HsShaderLightFunctionsCode.SPECULAR_LIGHT_SHADER_FUNCTIONS_CODE);
    this.fragmentFields.push(HsShaderLightFunctions.makeUniformArrays(this.shader.lightStorage.lights));
    this.fragmentFunctions.push(`vec3 createSpecularLights(vec3 normal, vec3 intersectionDirection, vec3 position) {
      vec3 lightColor = vec3(1.0, 1.0, 1.0);
      vec3 specular = vec3(0.0);
      ${HsShaderLightFunctions.makeLight(this.shader.lightStorage.lights, true)}
      return specular;
    }`);
    this.fragmentMain.push(
      `color.rgb += ${intensityString} * createSpecularLights(normal, intersectionDirection, v_position.xyz);`
    );
    return this;
  }

  public addBackgroundReflection(intensity: number = 1): HsShaderData {
    const intensityString: string = floatToCode(intensity);
    this.fragmentFunctions.push(`vec3 sky(vec3 rd) {
      float height = rd.y * 0.5 + 0.5;
      vec3 sky = vec3(0.6, 0.8, 1.0) * 4.0;
      vec3 sunColor = vec3(1.0, 0.75, 0.5) * 6.0;
      vec3 sunDir = normalize(vec3(1.0, 1.0, 1.0));
      sky += pow(max(dot(rd, sunDir), 0.0), 2.0) * sunColor;
      vec3 horizon = vec3(1.0, 1.0, 1.0);
      vec3 ground = vec3(0.8, 0.75, 0.65);
      float skyGradient = smoothstep(0.7, 0.4, height);
      float groundGradient = smoothstep(0.5, 0.3, height);
      vec3 color = mix(sky, horizon, skyGradient);
      color = mix(color, ground, groundGradient);
      return color;
    }`);
    this.fragmentMain.push(
      `color.rgb *= mix(vec3(1.0), sqrt(sky(reflect(intersectionDirection, normal)) * 0.72), ${intensityString});`
    );
    return this;
  }

  public addFresnel(intensity: number = 1): HsShaderData {
    const intensityString: string = floatToCode(intensity);
    this.fragmentFunctions.push(`vec3 fresnel(vec3 color, vec3 normal, vec3 direction) {
      float value = clamp(1.0 + dot(direction, normal), 0.0, 1.0);
			return color + ${intensityString} * value * value * (0.25 + 0.5 * color.rgb);
    }`);
    this.fragmentMain.push('color.rgb = fresnel(color.rgb, normal, intersectionDirection);');
    return this;
  }

  public addColorGrading(exposure: number = 0, contrast: number = 1): HsShaderData {
    this.fragmentMain.push(
      `color.rgb = (color.rgb * exp2(${floatToCode(exposure)}) - 0.5) * ${floatToCode(contrast)} + 0.5;`
    );
    return this;
  }

  public addPoints(): HsShaderData {
    this.vertexFields.push('in vec3 a_pointColors;');
    this.vertexFields.push('uniform float u_pointSize;');
    this.vertexMain.push('gl_PointSize = u_pointSize;');
    this.vertexMain.push('v_color = a_pointColors;');
    this.fragmentMain.push(`vec2 uv = gl_PointCoord * 2.0 - 1.0;
    float distanceUV = length(uv);
    if(distanceUV > 1.0){
      discard;
    }`);
    return this;
  }

  public addTexture(alpha: number = 1): HsShaderData {
    this.vertexFields.push('in vec2 a_texcoord;');
    this.vertexFields.push('out vec2 v_texcoord;');
    this.vertexMain.push('v_texcoord = a_texcoord;');
    this.fragmentFields.push('uniform sampler2D u_texture;');
    this.fragmentFields.push('uniform vec4 u_textureRect;');
    this.fragmentFields.push('in vec2 v_texcoord;');
    this.fragmentMain.push('vec4 textureColor = texture(u_texture, v_texcoord * u_textureRect.zw + u_textureRect.xy);');
    this.fragmentMain.push(`color.a *= ${floatToCode(alpha)};`);
    this.fragmentMain.push('color.rgb *= textureColor.rgb;');
    this.fragmentMain.push('color.a *= textureColor.a;');
    return this;
  }

  public setLightStorage(lightStorage: HsLightStorage): HsShaderData {
    this.shader.lightStorage = lightStorage;
    return this;
  }

  public setClippingStorage(clippingStorage: HsClippingStorage): HsShaderData {
    this.shader.clippingStorage = clippingStorage;
    return this;
  }

  public makeShader(): HsDefaultShader {
    this.shader.makeVertexCode = () => this.makeVertexCode();
    this.shader.makeFragmentCode = () => `#version 300 es
      precision highp float;

      in vec4 v_position;

      layout(location = 0) out vec4 fragmentColor;

      ${this.fragmentFields.join('\n')}

      ${this.fragmentFunctions.join('\n')}

      void main() {
        vec4 color = vec4(1.0, 1.0, 1.0, 1.0);
        ${this.fragmentMain.join('\n')}
        fragmentColor = color;
      }
      `;
    if (
      this.shader.shaderType === HsShaderObjectType.single &&
      this.vertexFields.includes('uniform vec3 u_color;') &&
      !this.vertexFields.includes('in vec2 a_texcoord;') &&
      !this.vertexFields.includes('in vec3 a_pointColors;')
    ) {
      this.shader.makeMeshBatchShader = (mode: HsMeshBatchMode) => {
        const shader: HsDefaultShader = new HsDefaultShader(HsShaderObjectType.collection);
        shader.lightStorage = this.shader.lightStorage;
        shader.clippingStorage = this.shader.clippingStorage;
        shader.hasTransparency = this.shader.hasTransparency;
        shader.makeVertexCode = () => this.makeVertexCode(mode);
        shader.makeFragmentCode = this.shader.makeFragmentCode;
        return shader;
      };
    } else {
      this.shader.makeMeshBatchShader = null;
    }
    return this.shader;
  }

  private makeVertexCode(batchMode?: HsMeshBatchMode): string {
    const matrixFields: string =
      batchMode !== undefined
        ? 'uniform highp sampler2D u_batchData; uniform int u_batchBase;'
        : hsAddVertexUniform('relativeMatrix', HsShaderDataType.mat4, this.shader.shaderType);
    const matrixValue: string =
      batchMode !== undefined
        ? `int batchId = u_batchBase + ${batchMode === 'instances' ? 'gl_InstanceID' : 'gl_DrawID'};
           ivec2 batchTexel = ivec2(
             (batchId % ${HS_MESH_BATCH_OBJECTS_PER_ROW}) * ${HS_MESH_BATCH_PIXELS_PER_OBJECT},
             batchId / ${HS_MESH_BATCH_OBJECTS_PER_ROW});
           mat4 relativeMatrix = mat4(
            texelFetch(u_batchData, batchTexel, 0),
            texelFetch(u_batchData, batchTexel + ivec2(1, 0), 0),
            texelFetch(u_batchData, batchTexel + ivec2(2, 0), 0),
            texelFetch(u_batchData, batchTexel + ivec2(3, 0), 0));`
        : hsGetMatrixUniform('relativeMatrix', HsShaderDataType.mat4, this.shader.shaderType);
    const singleMatrix: boolean = batchMode !== undefined || this.shader.shaderType === HsShaderObjectType.single;
    return `#version 300 es
      ${batchMode === 'multi-draw' ? '#extension GL_ANGLE_multi_draw : require' : ''}
      precision highp float;
  
      in vec3 a_positions;
  
      uniform mat4 u_projectionMatrix;
      uniform vec3 u_cameraPosition;

      ${matrixFields}
  
      ${this.vertexFields
        .map((field: string) => (batchMode !== undefined && field === 'uniform vec3 u_color;' ? '' : field))
        .join('\n')}
  
      out vec4 v_position;
  
      ${this.vertexFunctions.join('\n')}

      void main() {
        ${matrixValue}
        mat4 itemMatrix = relativeMatrix;
        itemMatrix[3].xyz ${singleMatrix ? '+' : '-'}= u_cameraPosition;
        ${singleMatrix ? '' : 'relativeMatrix = itemMatrix;'}
        ${
          // v_position is the negated world position; a collection's itemMatrix is camera-relative by now.
          singleMatrix
            ? 'v_position = -itemMatrix * vec4(a_positions, 1.0);'
            : 'v_position = -(itemMatrix * vec4(a_positions, 1.0) + vec4(u_cameraPosition, 0.0));'
        }
        gl_Position = (u_projectionMatrix * relativeMatrix) * vec4(a_positions, 1.0);
        ${this.vertexMain
          .map((line: string) =>
            batchMode !== undefined && line === 'vec3 color = u_color;'
              ? 'vec3 color = texelFetch(u_batchData, batchTexel + ivec2(4, 0), 0).rgb;'
              : line
          )
          .join('\n')}
      }
      `;
  }
}
